/**
 * Invitations à jeton — organisation OU équipe, un seul composant pour les deux portées.
 * Émission, lien à transmettre, et invitations en attente (organisation uniquement, cf. plus bas).
 *
 * Pourquoi le lien est affiché en évidence. exiPlan n'envoie **aucun e-mail**
 * (`invitations.service.js` ne fait que créer le jeton) : sans ce lien sous les yeux de
 * l'émetteur, une invitation créée n'atteint jamais son destinataire. Et le jeton en clair
 * n'est pas stocké côté serveur — il ne vit que dans l'état local de ce composant, donc il
 * n'est montré qu'une fois. Recharger la liste le perd définitivement, comme le veut le
 * serveur : c'est écrit à l'écran, pas seulement ici.
 *
 * Le serveur plafonne de toute façon le rôle accordé au rôle réel de l'émetteur ; le
 * sélecteur reçoit `assignableRoles` pour ne pas proposer une option qui rendrait un 403.
 *
 * Portée équipe : `POST /teams/:id/invitations` existe, mais il n'y a **aucune** route
 * `GET /teams/:id/invitations` côté serveur à ce jour — seule la portée organisation liste ses
 * invitations en attente. La section « en attente » est donc masquée en portée équipe plutôt
 * que d'appeler une route qui n'existe pas ; à réévaluer si le serveur l'ajoute un jour.
 *
 * Mode `registre` (lot O3, portée organisation) : l'invitation est créée au registre du studio,
 * qui ne rend le jeton qu'à la création (`invitationUrl`) et n'expose AUCUNE liste d'invitations
 * en attente (exiAdmin#85). On ne liste donc que celles créées depuis cet écran, jusqu'au
 * rechargement ; la révocation vise l'identifiant du registre
 * (`DELETE /organizations/:id/invitations/:invitationId`). `lectureSeule` (registre dégradé)
 * désactive l'envoi et la révocation.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { appAlert, appConfirm } from '../dialogs/service.js';
import { messageModeleOrganisation } from '../lib/org-errors.js';
import { LoadError, TruncatedListWarning } from './common.jsx';
import {
  BTN_GHOST,
  BTN_GHOST_SM,
  FORM_FIELD,
  FORM_LABEL,
  FORM_ROW,
  INPUT,
  SELECT,
} from '../ui/theme.js';

/** Mêmes clés que `RoleBadge` — un `<option>` ne peut pas accueillir le composant. */
const ROLE_OPTION_LABEL_KEYS = {
  proprietaire: 'roleBadge.proprietaire',
  administrateur: 'roleBadge.administrateur',
  facturation: 'roleBadge.facturation',
  editeur: 'roleBadge.editeur',
  lecteur: 'roleBadge.lecteur',
  owner: 'roleBadge.owner',
  admin: 'roleBadge.admin',
  member: 'roleBadge.member',
  viewer: 'roleBadge.viewer',
};

/**
 * Clés i18n par portée — littérales dans l'objet, comme `TeamsPage::ROLE_KEYS` : le gate
 * `keys.test.js` ne voit pas une clé de gabarit calculée (`t(variable)`), donc l'accès se fait
 * par une valeur déjà écrite en toutes lettres ci-dessous.
 */
const SCOPE_KEYS = {
  organization: {
    title: 'organization.invitations.title',
    emailLabel: 'organization.invitations.emailLabel',
    emailPlaceholder: 'organization.invitations.emailPlaceholder',
    roleLabel: 'organization.invitations.roleLabel',
    invite: 'organization.invitations.invite',
    inviteFailedTitle: 'organization.invitations.inviteFailedTitle',
    inviteFailed: 'organization.invitations.inviteFailed',
    linkWarning: 'organization.invitations.linkWarning',
    copyLink: 'organization.invitations.copyLink',
    linkCopied: 'organization.invitations.linkCopied',
    pendingTitle: 'organization.invitations.pendingTitle',
    pendingEmpty: 'organization.invitations.pendingEmpty',
    revoke: 'organization.invitations.revoke',
    revokeAria: 'organization.invitations.revokeAria',
    revokeConfirm: 'organization.invitations.revokeConfirm',
    revokeFailedTitle: 'organization.invitations.revokeFailedTitle',
    revokeFailed: 'organization.invitations.revokeFailed',
  },
  team: {
    title: 'teams.invitations.title',
    emailLabel: 'teams.invitations.emailLabel',
    emailPlaceholder: 'teams.invitations.emailPlaceholder',
    roleLabel: 'teams.invitations.roleLabel',
    invite: 'teams.invitations.invite',
    inviteFailedTitle: 'teams.invitations.inviteFailedTitle',
    inviteFailed: 'teams.invitations.inviteFailed',
    linkWarning: 'teams.invitations.linkWarning',
    copyLink: 'teams.invitations.copyLink',
    linkCopied: 'teams.invitations.linkCopied',
    pendingTitle: 'teams.invitations.pendingTitle',
    pendingEmpty: 'teams.invitations.pendingEmpty',
    revoke: 'teams.invitations.revoke',
    revokeAria: 'teams.invitations.revokeAria',
    revokeConfirm: 'teams.invitations.revokeConfirm',
    revokeFailedTitle: 'teams.invitations.revokeFailedTitle',
    revokeFailed: 'teams.invitations.revokeFailed',
  },
};

/**
 * `GET .../invitations` renvoie tout l'historique, acceptées et révoquées comprises. Les
 * afficher comme « en attente » ferait croire à des accès pendants qui n'existent pas.
 */
export function estEnAttente(invitation) {
  if (invitation.acceptedAt || invitation.revokedAt) return false;
  return !invitation.expiresAt || new Date(invitation.expiresAt).getTime() > Date.now();
}

/**
 * @param {object} props
 * @param {'organization'|'team'} [props.scope] Portée de l'invitation — gouverne l'URL et les clés i18n.
 * @param {string} props.scopeId Identifiant de l'organisation ou de l'équipe.
 * @param {string[]} props.assignableRoles Rôles que l'émetteur a le droit d'accorder.
 * @param {boolean} [props.registre] Mode `registre` (portée organisation seulement).
 * @param {boolean} [props.lectureSeule] Registre dégradé : aucun geste.
 * @param {import('react').Ref<HTMLInputElement>} [props.emailInputRef] Champ e-mail du
 *   formulaire : le primaire d'en-tête « Inviter un membre » de la page hôte lui donne le focus
 *   (règle de cohérence 1.3.0 — le primaire vit dans l'en-tête, ce formulaire garde un
 *   « Inviter » fantôme).
 */
export function OrgInvitations({
  scope = 'organization',
  scopeId,
  assignableRoles,
  emailInputRef,
  registre = false,
  lectureSeule = false,
}) {
  const { t } = useTranslation();
  const keys = SCOPE_KEYS[scope];
  const basePath =
    scope === 'team' ? `/teams/${scopeId}/invitations` : `/organizations/${scopeId}/invitations`;
  // Cf. note de tête de fichier : aucune route de listage n'existe côté serveur en portée équipe.
  const canListPending = scope === 'organization' && !registre;
  // Mode `registre` : pas de liste serveur, seulement ce qui a été créé depuis cet écran.
  const listeDeSession = scope === 'organization' && registre;

  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  // Rôle d'équipe (`member`) ou d'organisation (`editeur`, nomenclature D3) selon la portée.
  const [role, setRole] = useState(scope === 'team' ? 'member' : 'editeur');
  const [invitationUrl, setInvitationUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  /** Invitations créées pendant cette session (mode `registre`), `{ id, email, role, expire_le }`. */
  const [creees, setCreees] = useState([]);

  const { data, error, reload } = useAsync(
    (signal) =>
      canListPending ? fetchAllPages(basePath, { signal }) : Promise.resolve({ data: [] }),
    [basePath, canListPending],
  );
  const pending = (data?.data ?? []).filter(estEnAttente);

  const sendInvite = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data: created } = await api.post(basePath, {
        email: email.trim(),
        role,
        // `canDelete` n'a de sens que pour une invitation d'équipe (surcharge de suppression) ;
        // l'organisation ignore cette clé (schéma Zod sans elle).
        ...(scope === 'team' ? { canDelete: false } : {}),
      });
      setInvitationUrl(created.invitationUrl);
      if (listeDeSession && created.invitation?.id) {
        setCreees((liste) => [
          ...liste.filter((item) => item.id !== created.invitation.id),
          created.invitation,
        ]);
      }
      setCopied(false);
      setEmail('');
      await reload();
    } catch (error) {
      await appAlert(
        messageModeleOrganisation(error, t) ?? t(keys.inviteFailed, { message: error.message }),
        { title: t(keys.inviteFailedTitle) },
      );
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(invitationUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const revoke = async (invitation) => {
    const confirme = await appConfirm(t(keys.revokeConfirm, { email: invitation.email }), {
      title: t(keys.revokeFailedTitle),
    });
    if (!confirme) return;
    setBusy(true);
    try {
      if (listeDeSession) {
        await api.delete(`${basePath}/${invitation.id}`);
        setCreees((liste) => liste.filter((item) => item.id !== invitation.id));
      } else {
        await api.delete(`/invitations/${invitation.id}`);
      }
      await reload();
    } catch (error) {
      await appAlert(t(keys.revokeFailed, { message: error.message }), {
        title: t(keys.revokeFailedTitle),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-3 border-t-2 border-line pt-4">
      <p className="font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase">
        {t(keys.title)}
      </p>

      <form onSubmit={sendInvite} className={FORM_ROW}>
        <label className={FORM_FIELD}>
          <span className={FORM_LABEL}>{t(keys.emailLabel)}</span>
          <input
            ref={emailInputRef}
            type="email"
            required
            className={`${INPUT} w-72`}
            placeholder={t(keys.emailPlaceholder)}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={busy}
          />
        </label>

        <label className={FORM_FIELD}>
          <span className={FORM_LABEL}>{t(keys.roleLabel)}</span>
          <select
            className={SELECT}
            value={role}
            onChange={(event) => setRole(event.target.value)}
            disabled={busy}
          >
            {assignableRoles.map((r) => (
              <option key={r} value={r}>
                {t(ROLE_OPTION_LABEL_KEYS[r])}
              </option>
            ))}
          </select>
        </label>

        {/* Famille MD de bout en bout (reference.md §6.0) : champ, sélecteur et bouton à 32 px. */}
        <button type="submit" className={BTN_GHOST} disabled={busy || lectureSeule}>
          {t(keys.invite)}
        </button>
      </form>

      {invitationUrl && (
        <div className="max-w-3xl border-2 border-accent-trait bg-accent-wash p-3">
          <p role="alert" className="text-sm font-semibold text-accent-ink">
            {t(keys.linkWarning)}
          </p>
          <p className="mt-2 font-mono text-[11px] break-all text-ink">{invitationUrl}</p>
          <button type="button" className={`${BTN_GHOST_SM} mt-2`} onClick={copyLink}>
            {t(keys.copyLink)}
          </button>
          {copied && <span className="ml-2 text-xs text-ink-soft">{t(keys.linkCopied)}</span>}
        </div>
      )}

      {listeDeSession && (
        <>
          <p className="text-xs text-ink-faint">{t('organization.invitations.registreNote')}</p>
          <p className="font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase">
            {t('organization.invitations.pendingTitleSession')}
          </p>
          {creees.length === 0 ? (
            <p className="text-sm text-ink-soft">
              {t('organization.invitations.pendingEmptySession')}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {creees.map((invitation) => (
                <li
                  key={invitation.id}
                  className="flex max-w-3xl flex-wrap items-center justify-between gap-2 border-b-2 border-line-soft py-2"
                >
                  <span className="text-sm text-ink">
                    {invitation.email}
                    <span className="ml-2 text-xs text-ink-faint">
                      {t(ROLE_OPTION_LABEL_KEYS[invitation.role] ?? 'roleBadge.member')}
                    </span>
                    {invitation.expire_le && (
                      <span className="ml-2 text-xs text-ink-faint">
                        {t('organization.invitations.expiresOn', {
                          date: new Date(invitation.expire_le).toLocaleDateString(),
                        })}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className={BTN_GHOST_SM}
                    aria-label={t(keys.revokeAria, { email: invitation.email })}
                    disabled={busy || lectureSeule}
                    onClick={() => revoke(invitation)}
                  >
                    {t(keys.revoke)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {canListPending && (
        <>
          {error && <LoadError error={error} onRetry={reload} />}
          {data?.truncated && <TruncatedListWarning />}
          <p className="font-mono text-[0.68rem] tracking-eyebrow text-ink-faint uppercase">
            {t(keys.pendingTitle)}
          </p>
          {pending.length === 0 ? (
            <p className="text-sm text-ink-soft">{t(keys.pendingEmpty)}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {pending.map((invitation) => (
                <li
                  key={invitation.id}
                  className="flex max-w-3xl flex-wrap items-center justify-between gap-2 border-b-2 border-line-soft py-2"
                >
                  <span className="text-sm text-ink">
                    {invitation.email}
                    <span className="ml-2 text-xs text-ink-faint">
                      {t(ROLE_OPTION_LABEL_KEYS[invitation.role] ?? 'roleBadge.member')}
                    </span>
                  </span>
                  <button
                    type="button"
                    className={BTN_GHOST_SM}
                    // Le libellé accessible nomme l'invité : le libellé seul se répète à
                    // l'identique sur chaque ligne et ne dit rien au lecteur d'écran.
                    aria-label={t(keys.revokeAria, { email: invitation.email })}
                    disabled={busy}
                    onClick={() => revoke(invitation)}
                  >
                    {t(keys.revoke)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
